import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PublicLayout from "@/components/layout/PublicLayout";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Worlds from "@/pages/Worlds";
import CategoryWorld from "@/pages/CategoryWorld";
import PopPandaGo from "@/pages/PopPandaGo";
import InnovationLab from "@/pages/InnovationLab";
import FlavourCreator from "@/pages/FlavourCreator";
import StudentOps from "@/pages/StudentOps";
import Careers from "@/pages/Careers";
import EventOrders from "@/pages/EventOrders";
import BulkOrders from "@/pages/BulkOrders";
import Franchise from "@/pages/Franchise";
import Feedback from "@/pages/Feedback";
import Contact from "@/pages/Contact";
import Journal from "@/pages/Journal";
import HallOfFame from "@/pages/HallOfFame";
import RequestStock from "@/pages/RequestStock";
import GrowthJourney from "@/pages/GrowthJourney";
import FlavourLab from "@/pages/FlavourLab";
import { ChallengesList, ChallengeDetail } from "@/pages/Challenges";
import Contributors from "@/pages/Contributors";
import RetailerPortal from "@/pages/RetailerPortal";
import Complaints from "@/pages/Complaints";
import PaymentPortal from "@/pages/PaymentPortal";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MysteryBox from "@/pages/MysteryBox";
import AISuggest from "@/pages/AISuggest";
import MemberAuth from "@/pages/MemberAuth";
import MemberDashboard from "@/pages/MemberDashboard";
import MemberSpin from "@/pages/MemberSpin";
import MemberVault from "@/pages/MemberVault";
import PayScratchPay from "@/pages/PayScratchPay";
import PayScratchCard from "@/pages/PayScratchCard";
import PayScratchReceipt from "@/pages/PayScratchReceipt";
import { MemberProvider } from "@/contexts/MemberContext";

import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminFlavors from "@/pages/admin/AdminFlavors";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";
import AdminPrototypes from "@/pages/admin/AdminPrototypes";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminCRM from "@/pages/admin/AdminCRM";
import AdminDemand from "@/pages/admin/AdminDemand";
import AdminWorlds from "@/pages/admin/AdminWorlds";
import AdminMilestones from "@/pages/admin/AdminMilestones";
import AdminLabFlavours from "@/pages/admin/AdminLabFlavours";
import AdminChallenges from "@/pages/admin/AdminChallenges";
import AdminContributors from "@/pages/admin/AdminContributors";
import AdminRetailers from "@/pages/admin/AdminRetailers";
import AdminSections from "@/pages/admin/AdminSections";
import AdminJournal from "@/pages/admin/AdminJournal";
import AdminComplaints from "@/pages/admin/AdminComplaints";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminAssets from "@/pages/admin/AdminAssets";
import AdminFeatureFlags from "@/pages/admin/AdminFeatureFlags";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminHeatRules from "@/pages/admin/AdminHeatRules";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminCoinRules from "@/pages/admin/AdminCoinRules";
import AdminScanner from "@/pages/admin/AdminScanner";
import AdminSpinWheels from "@/pages/admin/AdminSpinWheels";
import AdminLoyalty from "@/pages/admin/AdminLoyalty";
import AdminVault from "@/pages/admin/AdminVault";
import AdminPayScratch from "@/pages/admin/AdminPayScratch";
import AdminCash from "@/pages/admin/AdminCash";
import AdminCashHistory from "@/pages/admin/AdminCashHistory";
import AdminCashAudit from "@/pages/admin/AdminCashAudit";
import AdminCashSettings from "@/pages/admin/AdminCashSettings";
import AdminWishlist from "@/pages/admin/AdminWishlist";
import RequestProduct from "@/pages/RequestProduct";
import RequestProductPoster from "@/pages/RequestProductPoster";
import RequestedProducts from "@/pages/RequestedProducts";
import RequestedProductDetail from "@/pages/RequestedProductDetail";
import TrackRequest from "@/pages/TrackRequest";

import "@/App.css";

function RequireAdmin({ children, masterOnly = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen grid place-items-center text-brand-charcoal/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  // Staff can only access /admin/cash — redirect elsewhere
  if (user.role_level === "staff" && masterOnly) return <Navigate to="/admin/cash" replace />;
  return children;
}

function StaffOrDashboard() {
  const { user } = useAuth();
  if (user?.role_level === "staff") return <Navigate to="/admin/cash" replace />;
  return <AdminDashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <MemberProvider>
        <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/worlds" element={<Worlds />} />
            <Route path="/worlds/:slug" element={<CategoryWorld />} />
            <Route path="/pop-panda-go" element={<PopPandaGo />} />
            <Route path="/innovation-lab" element={<InnovationLab />} />
            <Route path="/flavour-creator" element={<FlavourCreator />} />
            <Route path="/flavour-lab" element={<FlavourLab />} />
            <Route path="/journey" element={<GrowthJourney />} />
            <Route path="/challenges" element={<ChallengesList />} />
            <Route path="/challenges/:id" element={<ChallengeDetail />} />
            <Route path="/contributors" element={<Contributors />} />
            <Route path="/dream-pops" element={<Navigate to="/flavour-creator" replace />} />
            <Route path="/products" element={<Navigate to="/worlds" replace />} />
            <Route path="/student-opportunities" element={<StudentOps />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/event-orders" element={<EventOrders />} />
            <Route path="/bulk-orders" element={<BulkOrders />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/complaints" element={<Complaints />} />
            <Route path="/pay" element={<PaymentPortal />} />
            <Route path="/pay/success" element={<PaymentSuccess />} />
            <Route path="/mystery-box" element={<MysteryBox />} />
            <Route path="/ai-suggest" element={<AISuggest />} />
            <Route path="/hall-of-fame" element={<HallOfFame />} />
            <Route path="/request-stock" element={<RequestStock />} />
            <Route path="/request-product" element={<RequestProduct />} />
            <Route path="/request-product/poster" element={<RequestProductPoster />} />
            <Route path="/requested-products" element={<RequestedProducts />} />
            <Route path="/requested-products/:slug" element={<RequestedProductDetail />} />
            <Route path="/track-request" element={<TrackRequest />} />
          </Route>

          <Route path="/member/register" element={<MemberAuth />} />
          <Route path="/member/login" element={<MemberAuth />} />
          <Route path="/member/dashboard" element={<MemberDashboard />} />
          <Route path="/member/spin" element={<MemberSpin />} />
          <Route path="/member/vault" element={<MemberVault />} />
          <Route path="/spin" element={<Navigate to="/member/spin" replace />} />
          <Route path="/vault" element={<Navigate to="/member/vault" replace />} />

          <Route path="/scratch" element={<Navigate to="/scratch/main" replace />} />
          <Route path="/scratch/:outletCode" element={<PayScratchPay />} />
          <Route path="/scratch/card/:token" element={<PayScratchCard />} />
          <Route path="/scratch/receipt/:paymentId" element={<PayScratchReceipt />} />

          <Route path="/retailer" element={<RetailerPortal />}>
            <Route path="portal" element={<RetailerPortal.Overview />} />
            <Route path="portal/orders" element={<RetailerPortal.Orders />} />
            <Route path="portal/promotions" element={<RetailerPortal.Promotions />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
            <Route index element={<StaffOrDashboard />} />
            <Route path="cash" element={<AdminCash />} />
            <Route path="cash/history" element={<RequireAdmin masterOnly><AdminCashHistory /></RequireAdmin>} />
            <Route path="cash/audit" element={<RequireAdmin masterOnly><AdminCashAudit /></RequireAdmin>} />
            <Route path="cash/settings" element={<RequireAdmin masterOnly><AdminCashSettings /></RequireAdmin>} />
            <Route path="wishlist" element={<RequireAdmin masterOnly><AdminWishlist /></RequireAdmin>} />
            <Route path="sections" element={<RequireAdmin masterOnly><AdminSections /></RequireAdmin>} />
            <Route path="journey" element={<RequireAdmin masterOnly><AdminMilestones /></RequireAdmin>} />
            <Route path="lab" element={<RequireAdmin masterOnly><AdminLabFlavours /></RequireAdmin>} />
            <Route path="challenges" element={<RequireAdmin masterOnly><AdminChallenges /></RequireAdmin>} />
            <Route path="contributors" element={<RequireAdmin masterOnly><AdminContributors /></RequireAdmin>} />
            <Route path="retailers" element={<RequireAdmin masterOnly><AdminRetailers /></RequireAdmin>} />
            <Route path="worlds" element={<RequireAdmin masterOnly><AdminWorlds /></RequireAdmin>} />
            <Route path="products" element={<RequireAdmin masterOnly><AdminProducts /></RequireAdmin>} />
            <Route path="flavors" element={<RequireAdmin masterOnly><AdminFlavors /></RequireAdmin>} />
            <Route path="submissions" element={<RequireAdmin masterOnly><AdminSubmissions /></RequireAdmin>} />
            <Route path="prototypes" element={<RequireAdmin masterOnly><AdminPrototypes /></RequireAdmin>} />
            <Route path="reviews" element={<RequireAdmin masterOnly><AdminReviews /></RequireAdmin>} />
            <Route path="leads" element={<RequireAdmin masterOnly><AdminLeads /></RequireAdmin>} />
            <Route path="inventory" element={<RequireAdmin masterOnly><AdminInventory /></RequireAdmin>} />
            <Route path="crm" element={<RequireAdmin masterOnly><AdminCRM /></RequireAdmin>} />
            <Route path="demand" element={<RequireAdmin masterOnly><AdminDemand /></RequireAdmin>} />
            <Route path="journal" element={<RequireAdmin masterOnly><AdminJournal /></RequireAdmin>} />
            <Route path="complaints" element={<RequireAdmin masterOnly><AdminComplaints /></RequireAdmin>} />
            <Route path="invoices" element={<RequireAdmin masterOnly><AdminInvoices /></RequireAdmin>} />
            <Route path="assets" element={<RequireAdmin masterOnly><AdminAssets /></RequireAdmin>} />
            <Route path="features" element={<RequireAdmin masterOnly><AdminFeatureFlags /></RequireAdmin>} />
            <Route path="campaigns" element={<RequireAdmin masterOnly><AdminCampaigns /></RequireAdmin>} />
            <Route path="heat-rules" element={<RequireAdmin masterOnly><AdminHeatRules /></RequireAdmin>} />
            <Route path="members" element={<RequireAdmin masterOnly><AdminMembers /></RequireAdmin>} />
            <Route path="rewards" element={<RequireAdmin masterOnly><AdminRewards /></RequireAdmin>} />
            <Route path="coin-rules" element={<RequireAdmin masterOnly><AdminCoinRules /></RequireAdmin>} />
            <Route path="scanner" element={<RequireAdmin masterOnly><AdminScanner /></RequireAdmin>} />
            <Route path="spin-wheels" element={<RequireAdmin masterOnly><AdminSpinWheels /></RequireAdmin>} />
            <Route path="loyalty" element={<RequireAdmin masterOnly><AdminLoyalty /></RequireAdmin>} />
            <Route path="vault" element={<RequireAdmin masterOnly><AdminVault /></RequireAdmin>} />
            <Route path="pay-scratch" element={<RequireAdmin masterOnly><AdminPayScratch /></RequireAdmin>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
      </MemberProvider>
    </AuthProvider>
  );
}
